import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import {
  CheckIcon,
  XCircle,
  ChevronDown,
  XIcon,
  WandSparkles,
} from "lucide-react";

import { cn } from "@/lib/utils";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";

// Track the currently open dropdown
let activeDropdownId: string | null = null;

/**
 * Variants for the multi-select component to handle different styles.
 * Uses class-variance-authority (cva) to define different styles based on "variant" prop.
 */
const multiSelectVariants = cva(
  "m-1 transition ease-in-out delay-150 hover:-translate-y-1 hover:scale-110 duration-300",
  {
    variants: {
      variant: {
        default:
          "border-foreground/10 text-foreground bg-card hover:bg-card/80",
        secondary:
          "border-foreground/10 bg-secondary text-secondary-foreground hover:bg-secondary/80",
        destructive:
          "border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80",
        inverted: "inverted",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

/**
 * Props for MultiSelect component
 */
interface MultiSelectProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof multiSelectVariants> {
  /**
   * An array of option objects to be displayed in the multi-select component.
   * Each option object has a label, value, and an optional icon.
   */
  options: {
    /** The text to display for the option. */
    label: string;
    /** The unique value associated with the option. */
    value: string;
    /** Optional icon component to display alongside the option. */
    icon?: React.ComponentType<{ className?: string }>;
  }[];

  /**
   * Callback function triggered when the selected values change.
   * Receives an array of the new selected values.
   */
  onValueChange: (value: string[]) => void;

  /** The default selected values when the component mounts. */
  defaultValue?: string[];

  /**
   * Placeholder text to be displayed when no values are selected.
   * Optional, defaults to "Select options".
   */
  placeholder?: string;

  /**
   * Animation duration in seconds for the visual effects (e.g., bouncing badges).
   * Optional, defaults to 0 (no animation).
   */
  animation?: number;

  /**
   * Maximum number of items to display. Extra selected items will be summarized.
   * Optional, defaults to 3.
   */
  maxCount?: number;

  /**
   * The modality of the popover. When set to true, interaction with outside elements
   * will be disabled and only popover content will be visible to screen readers.
   * Optional, defaults to false.
   */
  modalPopover?: boolean;

  /**
   * Additional class names to apply custom styles to the multi-select component.
   * Optional, can be used to add custom styles.
   */
  className?: string;

  /**
   * Whether to show the search input in the dropdown.
   * Optional, defaults to true.
   */
  showSearch?: boolean;

  /**
   * The current selected values of the multi-select component.
   * Optional, defaults to undefined.
   */
  value?: string[];

  /**
   * Unique identifier for this dropdown instance.
   * Required to track which dropdown is currently open.
   */
  id: string;

  /**
   * Values that are mutually exclusive with every other option. Selecting one
   * of these clears the rest, and selecting any other option clears them. Also
   * omitted from the Select All toggle and its checked-state calculation.
   * Used for sentinels like "Not Applicable".
   */
  exclusiveValues?: string[];
}

export const MultiSelect = React.forwardRef<
  HTMLButtonElement,
  MultiSelectProps
>(
  (
    {
      options,
      onValueChange,
      variant,
      value,
      defaultValue = [],
      placeholder = "Select options",
      animation = 0,
      maxCount = 3,
      modalPopover = true,
      className,
      showSearch = true,
      id,
      exclusiveValues = [],
      ...props
    },
    ref
  ) => {
    const [selectedValues, setSelectedValues] = React.useState<string[]>(
      value ?? defaultValue
    );
    const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);
    const [isAnimating, setIsAnimating] = React.useState(false);
    const containerRef = React.useRef<HTMLDivElement>(null);
    const [visibleCount, setVisibleCount] = React.useState(maxCount);

    React.useEffect(() => {
      if (value !== undefined) {
        setSelectedValues(value);
      }
    }, [value]);

    const handleInputKeyDown = (
      event: React.KeyboardEvent<HTMLInputElement>
    ) => {
      if (event.key === "Enter") {
        setIsPopoverOpen(true);
      } else if (event.key === "Backspace" && !event.currentTarget.value) {
        const newSelectedValues = [...selectedValues];
        newSelectedValues.pop();
        setSelectedValues(newSelectedValues);
        onValueChange(newSelectedValues);
      }
    };

    const toggleOption = (option: string) => {
      // Prevent selecting "No information provided"
      if (option === "No information provided") {
        // Only allow unselecting this option, not selecting it
        if (selectedValues.includes(option)) {
          const newSelectedValues = selectedValues.filter(
            (value) => value !== option
          );
          setSelectedValues(newSelectedValues);
          onValueChange(newSelectedValues);
        }
        return;
      }

      let newSelectedValues: string[];
      if (selectedValues.includes(option)) {
        newSelectedValues = selectedValues.filter((value) => value !== option);
      } else if (exclusiveValues.includes(option)) {
        newSelectedValues = [option];
      } else {
        newSelectedValues = [
          ...selectedValues.filter((value) => !exclusiveValues.includes(value)),
          option,
        ];
      }
      setSelectedValues(newSelectedValues);
      onValueChange(newSelectedValues);
    };

    const handleClear = () => {
      setSelectedValues([]);
      onValueChange([]);
    };

    const handleTogglePopover = (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!isPopoverOpen) {
        // Close any other open dropdown
        if (activeDropdownId && activeDropdownId !== id) {
          const event = new CustomEvent("closeDropdown", {
            detail: { id: activeDropdownId },
          });
          window.dispatchEvent(event);
        }
        activeDropdownId = id;
      } else {
        activeDropdownId = null;
      }
      setIsPopoverOpen((prev) => !prev);
    };

    React.useEffect(() => {
      const handleCloseDropdown = (e: CustomEvent) => {
        if (e.detail.id === id) {
          setIsPopoverOpen(false);
          activeDropdownId = null;
        }
      };

      window.addEventListener(
        "closeDropdown",
        handleCloseDropdown as EventListener
      );
      return () => {
        window.removeEventListener(
          "closeDropdown",
          handleCloseDropdown as EventListener
        );
      };
    }, [id]);

    // Dynamically calculate how many badges fit in a single line
    const measureRef = React.useRef<HTMLDivElement>(null);
    const wrapperRef = React.useRef<HTMLDivElement>(null);
    const controlsRef = React.useRef<HTMLDivElement>(null);

    React.useLayoutEffect(() => {
      const measure = measureRef.current;
      const wrapper = wrapperRef.current;
      const controls = controlsRef.current;
      if (!measure || !wrapper || selectedValues.length === 0) {
        setVisibleCount(maxCount);
        return;
      }

      const calculateVisible = () => {
        const badges = Array.from(
          measure.querySelectorAll<HTMLElement>('[data-measure="badge"]')
        );
        if (badges.length === 0) return;

        // Measure the actual "+X more" badge width from the hidden container
        const moreBadgeEl = measure.querySelector<HTMLElement>('[data-measure="more"]');
        const moreBadgeStyle = moreBadgeEl ? getComputedStyle(moreBadgeEl) : null;
        const moreBadgeWidth = moreBadgeEl
          ? moreBadgeEl.offsetWidth + (parseFloat(moreBadgeStyle!.marginLeft) || 0) + (parseFloat(moreBadgeStyle!.marginRight) || 0)
          : 80;

        // Use wrapper width minus controls width for accurate available space
        const controlsWidth = controls ? controls.offsetWidth : 70;
        const availableWidth = wrapper.clientWidth - controlsWidth;
        if (availableWidth <= 0) return;

        let usedWidth = 0;
        let count = 0;

        for (const badge of badges) {
          const style = getComputedStyle(badge);
          const marginLeft = parseFloat(style.marginLeft) || 0;
          const marginRight = parseFloat(style.marginRight) || 0;
          const badgeWidth = badge.offsetWidth + marginLeft + marginRight;
          const remaining = selectedValues.length - count;

          // If there are more badges after this one, reserve space for "+X more"
          if (
            remaining > 1 &&
            usedWidth + badgeWidth + moreBadgeWidth > availableWidth &&
            count > 0
          ) {
            break;
          }
          // If this is the last badge, it just needs to fit
          if (
            remaining === 1 &&
            usedWidth + badgeWidth > availableWidth &&
            count > 0
          ) {
            break;
          }

          usedWidth += badgeWidth;
          count++;
        }

        setVisibleCount(Math.min(count || 1, maxCount));
      };

      const observer = new ResizeObserver(calculateVisible);
      observer.observe(wrapper);
      calculateVisible();

      return () => observer.disconnect();
    }, [selectedValues, maxCount]);

    const toggleAll = () => {
      const selectableOptions = options.filter(
        (option) =>
          option.value !== "No information provided" &&
          !exclusiveValues.includes(option.value)
      );
      if (selectedValues.length === selectableOptions.length) {
        handleClear();
      } else {
        const allValues = selectableOptions.map((option) => option.value);
        setSelectedValues(allValues);
        onValueChange(allValues);
      }
    };

    return (
      <Popover
        open={isPopoverOpen}
        onOpenChange={(open) => {
          if (!open) {
            activeDropdownId = null;
          }
          setIsPopoverOpen(open);
        }}
        modal={modalPopover}
      >
        <PopoverTrigger asChild>
          <Button
            ref={ref}
            {...props}
            onClick={handleTogglePopover}
            className={cn(
              "flex w-full p-1 rounded-md border min-h-10 h-auto items-center justify-between bg-inherit hover:bg-inherit [&_svg]:pointer-events-auto overflow-hidden",
              className
            )}
          >
            {selectedValues.length > 0 ? (
              <div ref={wrapperRef} className="flex justify-between items-center w-full overflow-hidden">
                {/* Hidden measurement container — renders all badges off-screen to measure widths */}
                <div
                  ref={measureRef}
                  aria-hidden
                  className="flex flex-nowrap items-center fixed overflow-hidden"
                  style={{ visibility: "hidden", top: -9999, left: -9999, pointerEvents: "none" }}
                >
                  {selectedValues.slice(0, maxCount).map((value) => {
                    const option = options.find((o) => o.value === value);
                    return (
                      <Badge
                        key={value}
                        data-measure="badge"
                        className={cn("shrink-0", multiSelectVariants({ variant }))}
                      >
                        {option?.label || value}
                        <XCircle className="ml-2 h-4 w-4" />
                      </Badge>
                    );
                  })}
                  <Badge
                    data-measure="more"
                    className={cn("shrink-0", multiSelectVariants({ variant }))}
                  >
                    {`+ ${Math.max(selectedValues.length - 1, 1)} more`}
                  </Badge>
                </div>
                <div ref={containerRef} className="flex flex-nowrap items-center flex-1 min-w-0 overflow-hidden">
                  {selectedValues.slice(0, visibleCount).map((value) => {
                    const option = options.find((o) => o.value === value);
                    const IconComponent = option?.icon;
                    return (
                      <Badge
                        key={value}
                        data-tag="badge"
                        className={cn(
                          "shrink-0",
                          isAnimating ? "animate-bounce" : "",
                          multiSelectVariants({ variant })
                        )}
                        style={{ animationDuration: `${animation}s` }}
                      >
                        {IconComponent && (
                          <IconComponent className="h-4 w-4 mr-2" />
                        )}
                        {option?.label || value}
                        <XCircle
                          className="ml-2 h-4 w-4 cursor-pointer"
                          onClick={(event) => {
                            event.stopPropagation();
                            toggleOption(value);
                          }}
                        />
                      </Badge>
                    );
                  })}
                  {selectedValues.length > visibleCount && (
                    <Badge
                      className={cn(
                        "shrink-0 bg-transparent text-foreground border-foreground/1 hover:bg-transparent",
                        isAnimating ? "animate-bounce" : "",
                        multiSelectVariants({ variant })
                      )}
                      style={{ animationDuration: `${animation}s` }}
                    >
                      {`+ ${selectedValues.length - visibleCount} more`}
                    </Badge>
                  )}
                </div>
                <div ref={controlsRef} className="flex items-center justify-between shrink-0">
                  <XIcon
                    className="h-4 mx-2 cursor-pointer text-muted-foreground"
                    onClick={(event) => {
                      event.stopPropagation();
                      handleClear();
                    }}
                  />
                  <Separator
                    orientation="vertical"
                    className="flex min-h-6 h-full"
                  />
                  <ChevronDown className="h-4 mx-2 cursor-pointer text-muted-foreground" />
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between w-full mx-auto">
                <span className="text-sm text-muted-foreground mx-3">
                  {placeholder}
                </span>
                <ChevronDown className="h-4 cursor-pointer text-muted-foreground mx-2" />
              </div>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-auto p-0"
          align="start"
          onEscapeKeyDown={() => {
            setIsPopoverOpen(false);
            activeDropdownId = null;
          }}
        >
          <Command>
            {showSearch && (
              <CommandInput
                placeholder="Search..."
                onKeyDown={handleInputKeyDown}
              />
            )}
            <CommandList>
              <CommandEmpty>No results found.</CommandEmpty>
              <CommandGroup>
                <CommandItem
                  key="all"
                  value="select-all"
                  onSelect={toggleAll}
                  className="cursor-pointer"
                >
                  <div
                    className={cn(
                      "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
                      selectedValues.length === options.filter((o) => o.value !== "No information provided" && !exclusiveValues.includes(o.value)).length
                        ? "bg-primary text-primary-foreground"
                        : "opacity-50 [&_svg]:invisible"
                    )}
                  >
                    <CheckIcon className="h-4 w-4" />
                  </div>
                  <span>(Select All)</span>
                </CommandItem>
                {options
                  .filter((option) => option.value !== "No information provided")
                  .map((option) => {
                    const isSelected = selectedValues.includes(option.value);
                    return (
                      <CommandItem
                        key={option.value}
                        value={option.label}
                        onSelect={() => toggleOption(option.value)}
                        className="cursor-pointer"
                      >
                        <div
                          className={cn(
                            "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
                            isSelected
                              ? "bg-primary text-primary-foreground"
                              : "opacity-50 [&_svg]:invisible"
                          )}
                        >
                          <CheckIcon className="h-4 w-4" />
                        </div>
                        {option.icon && (
                          <option.icon className="mr-2 h-4 w-4 text-muted-foreground" />
                        )}
                        <span>{option.label}</span>
                      </CommandItem>
                    );
                  })}
              </CommandGroup>
              <CommandSeparator />
              <CommandGroup>
                <div className="flex items-center justify-between">
                  {selectedValues.length > 0 && (
                    <>
                      <CommandItem
                        onSelect={handleClear}
                        className="flex-1 justify-center cursor-pointer"
                      >
                        Clear
                      </CommandItem>
                      <Separator
                        orientation="vertical"
                        className="flex min-h-6 h-full"
                      />
                    </>
                  )}
                  <CommandItem
                    onSelect={() => {
                      setIsPopoverOpen(false);
                      activeDropdownId = null;
                    }}
                    className="flex-1 justify-center cursor-pointer max-w-full"
                  >
                    Close
                  </CommandItem>
                </div>
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
        {animation > 0 && selectedValues.length > 0 && (
          <WandSparkles
            className={cn(
              "cursor-pointer my-2 text-foreground bg-background w-3 h-3",
              isAnimating ? "" : "text-muted-foreground"
            )}
            onClick={() => setIsAnimating(!isAnimating)}
          />
        )}
      </Popover>
    );
  }
);

MultiSelect.displayName = "MultiSelect";
